/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useState, useEffect, useCallback, useRef, type RefAttributes, useMemo } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import Hidden from '@/components/Hidden';
import { localStorage } from '@/utils/localStorage';
import { Grid } from '@/components/Grid';
import { SmartLink as Link } from '@/utils/smartLink';
import { usePathname, useSearchParams } from 'next/navigation';
import { isRouteProtected } from '@/protectedRoutes';
import AZLogo from '@/components/AZCustomComponent/AZLogo';
import { SearchBoxComponent } from '@/features/topNav/components/SearchBoxComponent';
import { Cart } from '@/features/topNav/components/Cart';
import { HeaderPromo } from '../HeaderPromo';
import { routePaths } from '@/constants/routePaths';
import { dataTestIds } from './constants';
import { useLabel } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { FeatureFlag } from '@/features/globalConfig';
import { Toast } from '@/components/Toast';
import { MenuNav } from './MenuNav/MenuNav';
import { useScrollBelowThreshold } from '@/hooks/useScrollBelowThreshold';
import styles from './TopHeaderV2.module.scss';
import L0BotLinks from './L0BotLinks';
import type { LoginMessageEventData } from '../../interface';
import { MobileHeaderSignIn } from './MobileHeaderSignIn/MobileHeaderSignIn';
import { useAppData } from '@/hooks/useAppData';
import { type FixedTopHeaderProps } from './FixedHeader/FixedHeader';
import dynamic from 'next/dynamic';
import { useCMSHeaderContent } from '../../api/getCMSHeaderContent';
import { CMSExperienceInjector } from '../../../contentstack/components/CMSExperienceInjector/CMSExperienceInjector';
import { countryCodes } from '@/constants/locale';
import cx from 'classnames';
import { useIsMobileQRExperience } from '@/hooks/useIsMobileQRExperience';
import type { ContentStackHeaderPromo } from '@/features/contentstack/interface';
import CMSSitewideAlertBanner from './contentstack/CMSSiteWideAlertBanner/CMSSitewideAlertBanner';
import { TopNavStore } from '@/features/topNav/components/Store';
import { Vehicle } from '@/features/topNav/components/Vehicle';
import type { VehicleSummary } from '@/types';
import { useHeaderData } from '../../api/getHeader';

type Props = {
  headerPromo: ContentStackHeaderPromo | undefined;
  showToast: boolean;
  preferredVehicle?: VehicleSummary | undefined;
  rewardsBalance?: number | undefined;
};

const FixedTopHeader = dynamic<FixedTopHeaderProps & RefAttributes<HTMLDivElement | null>>(
  () => import('./FixedHeader/FixedHeader'),
);

const LazyMyAccount = dynamic(
  () => import('@/components/MyAccount/myAccount').then((mod) => mod.MyAccount),
  { ssr: false },
);

const LazySignInAlertDesktop = dynamic(() =>
  import('@/features/header/components/TopHeader/SignInAlert/SignInAlertDesktop').then(
    (mod) => mod.SignInAlertDesktop,
  ),
);
const LazySignInAlertMobile = dynamic(() =>
  import('@/features/header/components/TopHeader/SignInAlert/SignInAlertMobile').then(
    (mod) => mod.SignInAlertMobile,
  ),
);

export const TopHeaderV2 = ({ headerPromo, showToast, preferredVehicle }: Props) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { appData } = useAppData();
  const { deviceType } = appData;
  const searchTextParam = searchParams.get('searchText') ?? '';
  const [searchText, setSearchText] = useState(searchTextParam);
  const [suggestionText, setSuggestionText] = useState(searchTextParam);
  const [scrolled, setScrolled] = useState(false);
  const [showSignInAlert, setShowSignInAlert] = useState(true);
  const [programaticallyScrolled, setProgramaticallyScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const [showDesktopDrawer, setShowDesktopDrawer] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isMenuFocused, setIsMenuFocused] = useState(false);
  const fixedHeaderRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const protectedRoute = pathname ? isRouteProtected(pathname) : false;
  const inactivitySignOutMsg = useLabel('label_you_have_been_signed_out_inactivity');
  const skipToMainContentLabel = useLabel('label_skip_to_main_content');
  const [isStickyHeaderMounted, setIsStickyHeaderMounted] = useState(false);
  const handleMenuToggle = useCallback((isMenuOpen: boolean) => {
    setShowDesktopDrawer(isMenuOpen);
    setIsMenuFocused(isMenuOpen);
  }, []);
  const headerRef = useRef<HTMLDivElement>(null);
  const myAccountContainerRef = useRef<HTMLDivElement>(null);
  const mobileMyAccountContainerRef = useRef<HTMLDivElement>(null);
  const alertBannerRef = useRef<HTMLDivElement>(null);
  const nonStickyHeaderRef = useRef<HTMLDivElement>(null);
  const vehicleText = preferredVehicle?.vehicleName || '';
  const currentSize = isMobile ? 'mobile' : 'desktop';
  const { data: cmsHeaderData } = useCMSHeaderContent();
  const locale = useLocale();
  const { data: headerData } = useHeaderData();
  const cartItemCount = headerData?.miniCartMap?.itemCount ?? 0;
  const hasCartItems = cartItemCount > 0;
  const isMobileQRExperience = useIsMobileQRExperience();

  const receiveMessage = useCallback((e: MessageEvent<LoginMessageEventData>) => {
    if (e.origin !== window.location.origin) {
      return;
    }

    if (e.data.type === 'loginsuccess') {
      const { pageType } = window.utag_data || {};

      localStorage.setItem('shouldDisplayLoginMsgOnReload', 'true');

      if (pageType !== 'checkout') {
        window.location.reload();
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', receiveMessage);
    return () => window.removeEventListener('message', receiveMessage);
  }, [receiveMessage]);

  useEffect(() => {
    const searchTextParam = searchParams.get('searchText') ?? '';
    if (!searchTextParam) {
      setSearchText('');
      setSuggestionText('');
    } else {
      setSuggestionText(searchTextParam);
    }
  }, [searchParams, setSearchText, setSuggestionText]);

  useEffect(() => {
    const handleResize = () => {
      if (nonStickyHeaderRef.current) {
        setHeaderHeight(nonStickyHeaderRef.current.getBoundingClientRect().height);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setHeaderHeight]);

  const scrollThreshold = useMemo(() => {
    const alertBannerHeight = alertBannerRef.current?.getBoundingClientRect().height || 0;
    return isMobile ? headerHeight / 2 : headerHeight - alertBannerHeight;
  }, [isMobile, headerHeight]);

  useScrollBelowThreshold(
    (belowThreshold) => {
      if (scrolled != belowThreshold || isMobile) {
        setScrolled(belowThreshold);
      }
    },
    [scrolled, headerHeight],
    scrollThreshold,
    showMobileDrawer,
  );

  useEffect(() => {
    if ((scrolled || programaticallyScrolled) && !isStickyHeaderMounted) {
      setIsStickyHeaderMounted(true);
    }
  }, [scrolled, programaticallyScrolled, isStickyHeaderMounted]);

  const handleMobileDrawer = (toggleState: boolean) => {
    setShowMobileDrawer(toggleState);
  };
  const headerPromoContent =
    headerPromo?.ad?.[0] !== undefined && headerPromo.ad[1] ? headerPromo : null;

  const mainContentID = 'main-content';

  const focusMainContent = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById(mainContentID)?.focus();
  };

  return (
    <div
      id="TopHeader"
      className={cx(styles.headerWrapper, {
        [styles.focusedHeader]: isMenuFocused || showMobileDrawer,
      })}
      ref={headerRef}
    >
      <Link
        to={`#${mainContentID}`}
        className={styles.skipToMainContentButton}
        onClick={focusMainContent}
      >
        {skipToMainContentLabel}
      </Link>

      <div id="headerNavWrapper" ref={nonStickyHeaderRef}>
        {cmsHeaderData?.sitewideBanner && cmsHeaderData.sitewideAlertBanner?.length ? (
          <div ref={alertBannerRef}>
            <CMSSitewideAlertBanner content={cmsHeaderData.sitewideAlertBanner[0]} />
          </div>
        ) : null}
        {cmsHeaderData?.experience_injector?.length ? (
          <CMSExperienceInjector content={cmsHeaderData.experience_injector[0]} />
        ) : null}
        {isStickyHeaderMounted ? (
          <FixedTopHeader
            scrolled={scrolled}
            programaticallyScrolled={programaticallyScrolled}
            updateShowToolTip={handleMenuToggle}
            isSearchFocused={isSearchFocused}
            ref={fixedHeaderRef}
            headerRef={headerRef}
            searchBoxComponent={
              <SearchBoxComponent
                isFixedHeader
                scrolled={scrolled}
                programaticallyScrolled={programaticallyScrolled}
                setProgramaticallyScrolled={setProgramaticallyScrolled}
                showMobileDrawer={handleMobileDrawer}
                isFocused={isSearchFocused}
                setIsFocused={setIsSearchFocused}
                searchText={searchText}
                onSearchTextChange={setSearchText}
                suggestionText={suggestionText}
                searchInputDataTestId={dataTestIds.desktopSearchInputFixedTopHeader}
                magniSearchBtnDataTestId={dataTestIds.locationSearchBtn}
                magniSearchBtnDataTestId2={dataTestIds.locationSearchBtnIsMobileSearchDrawer}
                onSuggestionTextChange={setSuggestionText}
              />
            }
            toastComponent={
              showToast ? (
                <Toast
                  color={'warning'}
                  message={inactivitySignOutMsg}
                  fixed={true}
                  closed={protectedRoute}
                  severity={'warning'}
                />
              ) : null
            }
            alertBannerComponent={
              cmsHeaderData?.sitewideBanner && cmsHeaderData.sitewideAlertBanner?.length ? (
                <CMSSitewideAlertBanner content={cmsHeaderData.sitewideAlertBanner[0]} />
              ) : null
            }
            vehicleComponent={
              <Vehicle
                dataTestIdDesktopVehicle={dataTestIds.deskTopVehicleMenuLg}
                currentSize={currentSize}
                vehicleText={vehicleText}
              />
            }
          />
        ) : null}
        <header id="mobile-top-header" data-testid="mobile-top-header" role="banner">
          <Grid container spacing={0}>
            {/* Promo for tablet/mobile — hidden on desktop via CSS */}
            {headerPromoContent ? (
              <Grid
                item
                xs={12}
                className={cx(styles.headerPromoBlock, styles.separator, {
                  [styles.withSitewideBanner]: cmsHeaderData?.sitewideBanner,
                })}
                id="header-promo-mobile"
              >
                <HeaderPromo content={headerPromoContent} />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <Grid container spacing={0} alignItems="center" id="topHeaderWrapper">
                <Grid item xs={12}>
                  <div
                    className={cx(styles.topHeaderWrapper, {
                      [styles.topHeaderWrapperWithCart]: hasCartItems,
                    })}
                  >
                    <div
                      data-testid="menu-nav-mobile"
                      className={`${styles.addInfoItems} ${styles.menuContainer}`}
                    >
                      <MenuNav
                        id="menuNavBtnScroll"
                        onToggle={handleMenuToggle}
                        dataTestIdBtn={dataTestIds.menuNavButtonMobile}
                        className={styles.menuNavWrapper}
                      />
                    </div>
                    <div className={styles.topNavTagline} data-testid="top-nav-tagline">
                      <Link id="azLogoLinkScroll" to={routePaths.blankURL}>
                        <AZLogo variant="Full" customClassName={styles.autoZoneLogo} />
                      </Link>
                    </div>
                    {/* Promo for desktop — hidden on tablet/mobile via CSS */}
                    {headerPromoContent ? (
                      <div
                        className={cx(styles.headerPromoInline, styles.separator, {
                          [styles.withSitewideBanner]: cmsHeaderData?.sitewideBanner,
                        })}
                        id="header-promo-desktop"
                      >
                        <HeaderPromo content={headerPromoContent} />
                      </div>
                    ) : null}
                    {locale !== countryCodes.ptBr ? (
                      <div className={styles.headerRightIcons}>
                        <div ref={mobileMyAccountContainerRef}>
                          <MobileHeaderSignIn />
                        </div>
                        <div className={styles.desktopWrapper}>
                          <Hidden implementation="js" smDown>
                            <div className={styles.myAccountCartSpacing}>
                              <div
                                className={`${styles.desktopAccountWrapper} ${styles.hoverInfoItems}`}
                                ref={myAccountContainerRef}
                              >
                                <LazyMyAccount setShowSignInAlert={setShowSignInAlert} />
                              </div>
                            </div>
                          </Hidden>
                          <FeatureFlag flag="SIGN_IN_ALERT_ENABLED">
                            {!scrolled &&
                              showSignInAlert &&
                              !isMobileQRExperience &&
                              (isMobile ? (
                                <LazySignInAlertMobile
                                  anchorRef={mobileMyAccountContainerRef.current}
                                  drawerOpen={showDesktopDrawer || showMobileDrawer}
                                />
                              ) : (
                                <LazySignInAlertDesktop
                                  anchorRef={myAccountContainerRef.current}
                                  drawerOpen={showDesktopDrawer}
                                />
                              ))}
                          </FeatureFlag>
                          <div className={`${styles.addInfoItems} ${styles.desktopCartWrapper}`}>
                            <Cart dataTestIdCartLink={dataTestIds.cartLinkTopHeaderWrapper} />
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <div className={styles.headerNavBar} id="non-sticky-header">
            <Grid container justify="center" alignItems="center" spacing={0}>
              <div className={`${styles.addInfo} ${styles.flexStartInfo}`} id="nav_wrapper">
                <div
                  data-testid="menu-nav-lg"
                  id="menu_nav"
                  className={cx(styles.addInfoItems, styles.hideOnMobileOnly, {
                    [styles.hidden]: isSearchFocused,
                  })}
                >
                  <MenuNav
                    id="hamburgerMenuBtn"
                    onToggle={handleMenuToggle}
                    dataTestIdBtn={dataTestIds.menuNavButton}
                    className={styles.menuNavWrapper}
                  />
                </div>
                <div
                  data-testid="vehicle-menu-lg"
                  className={cx(styles.addVehicleWrap, styles.hideOnMobileAndTablet, {
                    [styles.hidden]: isSearchFocused,
                  })}
                >
                  <Vehicle
                    dataTestIdDesktopVehicle={dataTestIds.deskTopVehicleMenuLg}
                    currentSize={currentSize}
                    vehicleText={vehicleText}
                  />
                </div>

                <div
                  className={`${styles.searchDesktopField} ${
                    isSearchFocused ? styles.searchDesktopFocused : ''
                  }`}
                >
                  <div className={styles.searchItem}>
                    <SearchBoxComponent
                      scrolled={scrolled}
                      programaticallyScrolled={programaticallyScrolled}
                      setProgramaticallyScrolled={setProgramaticallyScrolled}
                      isFocused={isSearchFocused}
                      setIsFocused={setIsSearchFocused}
                      showMobileDrawer={handleMobileDrawer}
                      searchText={searchText}
                      onSearchTextChange={setSearchText}
                      suggestionText={suggestionText}
                      searchInputDataTestId={dataTestIds.desktopSearchInput}
                      magniSearchBtnDataTestId={dataTestIds.locationSearchBtnScroll}
                      magniSearchBtnDataTestId2={dataTestIds.locatnSrchBtnScrollIsMobSrchDrawer}
                      onSuggestionTextChange={setSuggestionText}
                    />
                  </div>
                </div>
                <div
                  data-testid="store-menu-lg"
                  className={cx(styles.storeLocatorWrap, styles.hideOnMobileAndTablet, {
                    [styles.hidden]: isSearchFocused,
                  })}
                >
                  <TopNavStore dataTestIdStoreName={dataTestIds.storeNameTextTopHeader} />
                </div>
              </div>
            </Grid>
          </div>
        </header>
        {!scrolled && !programaticallyScrolled && showToast && (
          <Toast
            color={'warning'}
            message={inactivitySignOutMsg}
            closed={protectedRoute}
            severity={'warning'}
          />
        )}
      </div>
      {deviceType === 'bot' && <L0BotLinks />}
    </div>
  );
};

TopHeaderV2.displayName = 'TopHeaderV2';
